import React, { FunctionComponent, useEffect, useState, useRef } from 'react';

import classnames from 'classnames';
import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

import { Overlay } from 'core/overlay/Overlay';
import { ButtonVariant } from 'core/util/Bootstrap';
import { escapeStringRegex } from 'social/common/util/Util';
import { ErrorDisplay } from 'social/publisher/error-display/ErrorDisplay';
import { PublisherError, Label } from 'social/publisher/Publisher.types';
import { usePublisherDispatch } from 'social/publisher/PublisherContext';
import { fetchLabels } from 'social/publisher/PublisherFetchers';

import 'social/publisher/label-selector/LabelSelector.scss';

interface Props {
    selectedLabels: Label[];
    selectedLabelIds?: string[];
    tenantId: number | undefined;
    publisherErrors: PublisherError[];
    hasLabelAddPermission: boolean;
}

const OVERLAY_AREA = 'social-publisher-label-selector';
const LABEL_NAME_MIN_LENGTH = 3;
const sanitizeInput = (input: string): string => {
    return input.replace(/<[^>]*>/g, '');
};
const labelNameExists = (labelName: string, labels: Label[]): boolean => {
    return !!labels.find((el) => el.name === labelName);
};
const formatLabelName = (nameInput: string): string => {
    const trimmedInput = nameInput.trim();
    let formattedLabelName = '';

    if (trimmedInput.length > 0) {
        formattedLabelName = trimmedInput[0].toUpperCase() + trimmedInput.slice(1);
    }
    return formattedLabelName;
};
const highlightMatchedChars = (searchText: string, sourceText: string): JSX.Element => {
    const textSplit = sourceText.split(new RegExp(`(${escapeStringRegex(searchText)})`, 'gi'));

    return (
        <div>
            {textSplit.map((el, index) => (
                <span
                    key={`${el}-${index + 1}`}
                    className={classnames({ bold: el.toLowerCase() === searchText.toLowerCase() })}
                >
                    {el}
                </span>
            ))}
        </div>
    );
};

export const LabelSelector: FunctionComponent<Props> = ({
    selectedLabels,
    selectedLabelIds,
    tenantId,
    publisherErrors,
    hasLabelAddPermission,
}) => {
    const dispatch = usePublisherDispatch();
    const { t } = useTranslation(['social/publisher']);
    const labelInputRef = useRef<HTMLInputElement>(null);
    const [labels, setLabels] = useState<Label[]>([]);
    const [labelInput, setLabelInput] = useState('');
    const [matchedLabels, setMatchedLabels] = useState<Label[]>([]);
    const [isCreateLabel, setIsCreateLabel] = useState(false);
    const [isFocusInput, setIsFocusInput] = useState(false);
    const [displayedLabels, setDisplayedLabels] = useState(selectedLabels); // named as displayedLabels just coz it has to be different than the prop selectedLabels.

    const handleLabelInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const val = sanitizeInput(e.target.value);
        const query = val.trim();
        let matchedLabels: Label[] = [];

        if (query.length > 0) {
            matchedLabels = labels.filter((el) => el.name.toLowerCase().indexOf(query.toLowerCase()) > -1);
        }

        if (query.length >= LABEL_NAME_MIN_LENGTH && matchedLabels.length === 0) {
            setIsCreateLabel(true);
        } else {
            setIsCreateLabel(false);
        }

        setLabelInput(val);
        setMatchedLabels(matchedLabels);
    };

    const handleSelectLabel = (label: Label): void => {
        if (!labelNameExists(label.name, displayedLabels)) {
            const newDisplayedLabels = [...displayedLabels, label];

            dispatch({ type: 'set-selected-labels', payload: { value: newDisplayedLabels } });
            setDisplayedLabels(newDisplayedLabels);
        }

        setLabelInput('');
        setMatchedLabels([]);
    };

    const handleCreateLabel = (): void => {
        const labelsToAdd: Label[] = [];

        labelInput.split(',').forEach((el) => {
            const labelName = formatLabelName(el);

            if (!labelNameExists(labelName, displayedLabels)) {
                if (labelNameExists(labelName, labels)) {
                    const label = labels.find((el) => el.name === labelName) as Label;

                    labelsToAdd.push(label);
                } else if (labelName.length >= LABEL_NAME_MIN_LENGTH) {
                    labelsToAdd.push({
                        id: '',
                        name: labelName,
                    });
                }
            }
        });

        if (labelsToAdd.length > 0) {
            const newDisplayedLabels = [...displayedLabels, ...labelsToAdd];

            dispatch({ type: 'set-selected-labels', payload: { value: newDisplayedLabels } });
            setDisplayedLabels(newDisplayedLabels);
        }

        setLabelInput('');
        setIsCreateLabel(false);
    };

    const handleRemoveLabel = (label: Label): void => {
        const newDisplayedLabels = displayedLabels.filter((el) => el.name !== label.name);

        dispatch({ type: 'set-selected-labels', payload: { value: newDisplayedLabels } });
        setDisplayedLabels(newDisplayedLabels);
    };

    const handleLabelInputFocus = (): void => {
        setIsFocusInput(true);

        const labelsError = publisherErrors.filter((el) => el.component === 'LABELS');

        if (labelsError) {
            dispatch({ type: 'remove-publisher-errors-by-component', payload: { value: 'LABELS' } });
        }
    };

    const handleLabelInputBlur = (): void => {
        setIsFocusInput(false);
    };

    useEffect(() => {
        const loadLabels = async (): Promise<void> => {
            const data = await trackPromise(fetchLabels(tenantId), OVERLAY_AREA);
            // const labels = data.map((el) => el.label);

            setLabels(data);

            if (selectedLabelIds) {
                const labels = data.filter((label) => selectedLabelIds.indexOf(label.id) !== -1);

                setDisplayedLabels(labels);
            }
        };

        loadLabels();
    }, [tenantId, selectedLabelIds]);

    return (
        <div className="r4e-social-label-selector">
            <Overlay area={OVERLAY_AREA} />
            <h3 className="label-selector-title">
                <i className="icon icon-label" />
                {t('ADD_LABELS')}
            </h3>
            <ErrorDisplay component="LABELS" publisherErrors={publisherErrors} />
            <div
                className="labels-container"
                onClick={(): void => {
                    if (labelInputRef && labelInputRef.current) {
                        labelInputRef.current.focus();
                    }
                }}
                role="button"
                onKeyPress={(): void => {
                    console.log('do nothing');
                }}
                tabIndex={0}
            >
                {displayedLabels.length === 0 && !isFocusInput && labelInput === '' && (
                    <span className="placeholder">{t('LABELS')}</span>
                )}
                {displayedLabels.map((el) => (
                    <div className="selected-label">
                        <span className="selected-label-name">{el.name}</span>
                        <Button
                            variant={ButtonVariant.Icon}
                            className="remove-label-btn"
                            onClick={(): void => handleRemoveLabel(el)}
                        >
                            <i className="icon icon-times" />
                        </Button>
                    </div>
                ))}

                <div className="input-container">
                    <div className={classnames('label-matches', { hide: matchedLabels.length === 0 })}>
                        {matchedLabels.map((el) => (
                            <div
                                role="button"
                                tabIndex={0}
                                className="label-match"
                                key={el.id}
                                onClick={(): void => handleSelectLabel(el)}
                                onKeyPress={(): void => {
                                    console.log(el);
                                }}
                            >
                                {highlightMatchedChars(labelInput, el.name)}
                            </div>
                        ))}
                    </div>
                    {hasLabelAddPermission && (
                        <div className={classnames('create-label-btn-container', { hide: !isCreateLabel })}>
                            <Button
                                variant={ButtonVariant.Icon}
                                onClick={handleCreateLabel}
                                className="create-label-btn"
                            >
                                <i className="icon icon-plus-circle" />
                            </Button>
                        </div>
                    )}
                    <Form.Control
                        type="text"
                        value={labelInput}
                        onChange={handleLabelInputChange}
                        onFocus={handleLabelInputFocus}
                        onBlur={handleLabelInputBlur}
                        className="label-input"
                        ref={labelInputRef}
                    />
                </div>
            </div>
        </div>
    );
};
